/* perfil.tsx — el perfil de la empresa, que ahora vive en el servidor.
 *
 * Antes estaba en el `localStorage` del navegador y se reenviaba entero en cada búsqueda. Ahora es
 * de la CUENTA: el servidor lo lee solo y puntúa por su cuenta, así que el frontend NO manda
 * `perfil` ni `rup` en las peticiones al panel. Eso es lo que permite que no exista un gemelo del
 * puntaje en JavaScript.
 *
 * Se guarda con rebote porque la página de perfil es un formulario vivo —cada píldora de
 * departamento que se toca dispararía un PUT— y con control optimista por `version`, para que dos
 * pestañas no se pisen en silencio.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { borrar as borrarApi, ErrorApi, obtener, reemplazar } from '../api/cliente'
import type { Perfil, RespuestaPerfil, ResultadoOferta, RupDestilado } from '../api/tipos'

/** Perfil vacío. Existe para que la página de perfil no tenga que comprobar `null` en cada campo,
 *  y para que «sin perfil» sea un estado con forma en vez de una ausencia. */
export const PERFIL_VACIO: Perfil = {
  nit: '',
  razon_social: '',
  sectores: [],
  deps: [],
  min: null,
  max: null,
  rup: true,
  unspsc: [],
  temas_manuales: [],
  contratos_a_la_vez: null,
  dias_min_oferta: null,
  fuera_territorio: true,
  comparar: true,
  reglas_no_ver: [],
  descartes_agg: {},
  avisos: { frecuencia: 'diario', umbral_afinidad: 70, cambios_en_seguidos: true },
  watch: {},
}

type Estado = 'cargando' | 'listo' | 'guardando' | 'error' | 'conflicto'

interface EstadoPerfil {
  perfil: Perfil
  rup: RupDestilado | null
  /** ¿Hay algo guardado? Distinto de «el perfil está vacío»: sirve para ofrecer cargar el RUP. */
  existe: boolean
  estado: Estado
  error: string | null
  guardadoEn: number | null
  /** Cambia el perfil en local y agenda el guardado. Devuelve al instante: la interfaz no espera. */
  editar: (cambios: Partial<Perfil>) => void
  /** Fuerza el guardado ya (botón «Guardar»), sin esperar el rebote. */
  guardarYa: () => Promise<void>
  /** Reemplaza el RUP destilado (tras subir el PDF o estimar por NIT). */
  ponerRup: (rup: RupDestilado, perfilSugerido?: Partial<Perfil>) => void
  /** Marca un proceso como seguido o descartado; `null` quita la marca. `nota` la escribe
   *  «Guardar en la ficha» del asistente y se conserva al cambiar de estado. */
  marcar: (procesoId: string, estado: 'sigo' | 'desc' | null, motivo?: string, nota?: string) => void
  /** Desenlace de la oferta. No borra el seguimiento; sobrevive a la poda. `null` lo quita. */
  registrarResultado: (procesoId: string, resultado: ResultadoOferta | null) => void
  /**
   * Marcar en el perfil de OTRA empresa de la cartera, no en el activo.
   *
   * Existe por el modo «todas» del listado: ahí cada fila es de una empresa distinta, y descartar
   * una fila que salió «para Maderas» tiene que escribir en el watch de Maderas. Escribir en el
   * activo sería el fallo silencioso que el `empresa_id` explícito viene a evitar, agravado: la
   * persona vería el proceso desaparecer y la marca estaría en la empresa equivocada.
   *
   * No pasa por el estado local con rebote —ese es el de la empresa activa— sino por leer, cambiar
   * y guardar contra el servidor. Devuelve cuando terminó, para que quien llama pueda avisar.
   */
  marcarEn: (empresaId: number, procesoId: string,
             estado: 'sigo' | 'desc' | null, motivo?: string) => Promise<void>
  /** Limpia todo para cargar otra empresa. */
  limpiar: () => Promise<void>
  recargar: () => Promise<void>
}

const Contexto = createContext<EstadoPerfil | null>(null)

const REBOTE_MS = 800

/**
 * El cuerpo de un `PUT /api/perfil`.
 *
 * Vive aparte del componente para poder VIGILARLO (`perfil.test.ts`), y lo que se vigila es que
 * lleve `empresa_id`. Una cuenta puede tener varias empresas; cuál está activa vive en la SESIÓN,
 * que las pestañas COMPARTEN. Si otra pestaña cambia de empresa mientras esta edita, un guardado
 * «sobre la activa» acabaría en la empresa equivocada sin que nadie se entere.
 *
 * El servidor tiene la guardia —responde 409 si el `empresa_id` no es el activo— pero solo puede
 * aplicarla si se le manda: sin el campo cae al «la activa» por omisión, la defensa queda PASIVA y
 * el fallo vuelve entero. Es lo primero que hay que conservar al tocar esto.
 *
 * Y no basta con `version`: cada empresa tiene su propio contador, así que dos empresas distintas
 * pueden estar las dos en la versión 1 y el número coincidiría sin que sean lo mismo.
 *
 * `empresa_id` va AUSENTE —no en null— cuando la cuenta todavía no tiene ninguna empresa: ahí el
 * servidor crea la primera, que es lo que la persona está haciendo aunque no lo llame así.
 */
export function cuerpoDeGuardado(
  perfil: Perfil,
  rup: RupDestilado | null,
  version: number,
  empresaId: number | null,
) {
  return {
    perfil,
    rup: rup ?? undefined,
    version,
    empresa_id: empresaId ?? undefined,
  }
}

/** La ruta del `DELETE` del perfil, con la empresa dicha. Misma razón que arriba, y más fuerte:
 *  vaciar un perfil es destructivo y tiene que caer sobre la empresa que quien lo pulsa está
 *  mirando, no sobre la que otra pestaña dejó activa entretanto. */
export function rutaDeBorrado(empresaId: number | null): string {
  return empresaId === null ? '/api/perfil' : `/api/perfil?empresa_id=${empresaId}`
}

export function ProveedorPerfil({ children }: { children: ReactNode }) {
  const [perfil, setPerfil] = useState<Perfil>(PERFIL_VACIO)
  const [rup, setRup] = useState<RupDestilado | null>(null)
  const [existe, setExiste] = useState(false)
  const [estado, setEstado] = useState<Estado>('cargando')
  const [error, setError] = useState<string | null>(null)
  const [guardadoEn, setGuardadoEn] = useState<number | null>(null)

  // En refs y no en estado: son la verdad para el guardado, y meterlos en el ciclo de render haría
  // que cada tecla reprogramara el temporizador con un valor viejo.
  const version = useRef(0)
  /* A qué empresa de la cartera pertenece lo que se está editando. Va en cada `PUT` junto con la
     versión y NO es redundante con ella: ver `cuerpoDeGuardado`. */
  const empresaId_ = useRef<number | null>(null)
  const pendiente = useRef<{ perfil: Perfil; rup: RupDestilado | null } | null>(null)
  const temporizador = useRef<number | null>(null)
  // Espejo del último valor, actualizado de forma SÍNCRONA. Permite componer varias ediciones en
  // el mismo tick sin leer el estado dentro del updater de `setPerfil` —React pide que ese updater
  // sea puro— ni depender de un `perfil` de la clausura, que llegaría viejo.
  const ultimo = useRef<{ perfil: Perfil; rup: RupDestilado | null }>({
    perfil: PERFIL_VACIO,
    rup: null,
  })

  const recargar = useCallback(async () => {
    setEstado('cargando')
    try {
      const r = await obtener<RespuestaPerfil>('/api/perfil')
      version.current = r.version
      empresaId_.current = r.empresa_id
      const cargado = migrar({ ...PERFIL_VACIO, ...(r.perfil ?? {}) })
      ultimo.current = { perfil: cargado, rup: r.rup }
      setPerfil(cargado)
      setRup(r.rup)
      setExiste(r.perfil !== null)
      setGuardadoEn(r.actualizado_en)
      setEstado('listo')
      setError(null)
    } catch (e) {
      setEstado('error')
      setError(e instanceof ErrorApi ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    void recargar()
  }, [recargar])

  const guardar = useCallback(async (opciones: { alCerrar?: boolean } = {}) => {
    const porGuardar = pendiente.current
    if (!porGuardar) return
    pendiente.current = null
    setEstado('guardando')
    try {
      const r = await reemplazar<RespuestaPerfil>(
        '/api/perfil',
        cuerpoDeGuardado(porGuardar.perfil, porGuardar.rup, version.current, empresaId_.current),
        undefined,
        // Al ocultarse la pestaña el navegador puede descartar la página, y con ella una petición
        // normal a medio camino: el cambio se perdería en silencio, que es justo lo que este
        // guardado viene a evitar. `keepalive` deja que termine sin la página. Su límite de cuerpo
        // es de 64 KB, y por eso el servidor capa el perfil y el RUP a 32 KB cada uno.
        { keepalive: opciones.alCerrar === true },
      )
      version.current = r.version
      empresaId_.current = r.empresa_id
      setExiste(true)
      setGuardadoEn(r.actualizado_en)
      setEstado('listo')
      setError(null)
    } catch (e) {
      if (e instanceof ErrorApi && e.estado === 409) {
        // Otra pestaña guardó primero. No se reintenta a ciegas: eso es exactamente el pisotón
        // que el control de versión vino a evitar. Lo resuelve quien está mirando.
        setEstado('conflicto')
        setError(e.message)
        return
      }
      // Cualquier otro fallo (red caída, servidor en mantenimiento) es REINTENTABLE, así que lo
      // pendiente vuelve a la cola. Sin esto, quien ve «no se pudo guardar» pulsa «Guardar» y no
      // pasa nada —`guardar()` sale porque `pendiente` quedó en null— y solo revive editando otra
      // cosa. Se conserva lo más nuevo si mientras tanto se editó de nuevo.
      pendiente.current = pendiente.current ?? porGuardar
      setEstado('error')
      setError(e instanceof ErrorApi ? e.message : String(e))
    }
  }, [])

  const agendar = useCallback(
    (siguiente: Perfil, siguienteRup: RupDestilado | null) => {
      pendiente.current = { perfil: siguiente, rup: siguienteRup }
      if (temporizador.current !== null) window.clearTimeout(temporizador.current)
      temporizador.current = window.setTimeout(() => void guardar(), REBOTE_MS)
    },
    [guardar],
  )

  // `agendar` va FUERA del updater de `setPerfil`: React exige que ese updater sea puro y lo
  // ejecuta dos veces en StrictMode. El valor nuevo sale de `ultimo.current`, no de la clausura,
  // para que dos ediciones en el mismo tick se compongan en vez de pisarse.
  const editar = useCallback(
    (cambios: Partial<Perfil>) => {
      const siguiente = { ...ultimo.current.perfil, ...cambios }
      ultimo.current = { ...ultimo.current, perfil: siguiente }
      setPerfil(siguiente)
      agendar(siguiente, ultimo.current.rup)
    },
    [agendar],
  )

  const ponerRup = useCallback(
    (nuevo: RupDestilado, perfilSugerido?: Partial<Perfil>) => {
      const siguiente = { ...ultimo.current.perfil, ...perfilSugerido }
      ultimo.current = { perfil: siguiente, rup: nuevo }
      setRup(nuevo)
      setPerfil(siguiente)
      agendar(siguiente, nuevo)
    },
    [agendar],
  )

  const marcar = useCallback(
    (procesoId: string, estado: 'sigo' | 'desc' | null, motivo?: string, nota?: string) => {
      const watch = { ...ultimo.current.perfil.watch }
      const descartes = { ...ultimo.current.perfil.descartes_agg }
      const previa = watch[procesoId]
      if (estado === null) {
        delete watch[procesoId]
      } else {
        // La nota SOBREVIVE al cambio de estado: guardar la respuesta del asistente y después
        // descartar el proceso no puede borrar lo que se anotó — es justo el motivo por el que
        // alguien querría volver a mirarlo.
        watch[procesoId] = {
          estado,
          ...(motivo ? { motivo } : {}),
          ...(nota ?? previa?.nota ? { nota: nota ?? (previa?.nota as string) } : {}),
          ...(previa?.resultado ? { resultado: previa.resultado } : {}),
          ts: Date.now(),
        }
        // El CONTEO por motivo se acumula y no se descuenta al quitar la marca: de él salen las
        // reglas sugeridas de «Lo que no quieres ver», y esa sugerencia nace de cuántas veces has
        // descartado por lo mismo, no de cuántos siguen descartados ahora.
        if (estado === 'desc' && motivo) descartes[motivo] = (descartes[motivo] ?? 0) + 1
      }
      editar({ watch, descartes_agg: descartes })
    },
    [editar],
  )

  const registrarResultado = useCallback((
    procesoId: string, resultado: ResultadoOferta | null,
  ) => {
    const watch = { ...ultimo.current.perfil.watch }
    const previa = watch[procesoId]
    if (resultado === null) {
      if (!previa) return
      const resto = { ...previa }
      delete resto.resultado
      watch[procesoId] = resto
      if (!resto.estado && !resto.nota) delete watch[procesoId]
    } else {
      watch[procesoId] = {
        estado: previa?.estado ?? 'sigo',
        ...(previa?.motivo ? { motivo: previa.motivo } : {}),
        ...(previa?.nota ? { nota: previa.nota } : {}),
        resultado,
        ts: Date.now(),
      }
    }
    editar({ watch })
  }, [editar])

  const marcarEn = useCallback(async (
    empresaId: number, procesoId: string, estado: 'sigo' | 'desc' | null, motivo?: string,
  ) => {
    if (empresaId === empresaId_.current) {
      // Es la activa: por el camino normal, con su rebote y su estado en pantalla.
      marcar(procesoId, estado, motivo)
      return
    }
    /* Leer, cambiar y guardar contra el servidor. Un reintento y no más: si al reintentar sigue
       habiendo conflicto es que alguien más está escribiendo esa empresa a la vez, y en ese caso
       insistir en bucle pisaría a la otra pestaña — que es justo lo que el control de versión
       existe para no hacer. */
    for (let intento = 0; intento < 2; intento++) {
      const r = await obtener<RespuestaPerfil>(`/api/perfil?empresa_id=${empresaId}`)
      const watch = { ...(r.perfil?.watch ?? {}) }
      const descartes = { ...(r.perfil?.descartes_agg ?? {}) }
      if (estado === null) delete watch[procesoId]
      else {
        const previa = watch[procesoId]
        watch[procesoId] = {
          estado,
          ...(motivo ? { motivo } : {}),
          ...(previa?.nota ? { nota: previa.nota } : {}),
          ...(previa?.resultado ? { resultado: previa.resultado } : {}),
          ts: Date.now(),
        }
        if (estado === 'desc' && motivo) descartes[motivo] = (descartes[motivo] ?? 0) + 1
      }
      try {
        await reemplazar('/api/perfil', {
          perfil: { ...PERFIL_VACIO, ...(r.perfil ?? {}), watch, descartes_agg: descartes },
          version: r.version,
          empresa_id: empresaId,
        })
        return
      } catch (e) {
        if (!(e instanceof ErrorApi && e.estado === 409) || intento === 1) throw e
      }
    }
  }, [marcar])

  const guardarYa = useCallback(async () => {
    if (temporizador.current !== null) window.clearTimeout(temporizador.current)
    await guardar()
  }, [guardar])

  const limpiar = useCallback(async () => {
    if (temporizador.current !== null) window.clearTimeout(temporizador.current)
    pendiente.current = null
    await borrarApi(rutaDeBorrado(empresaId_.current))
    await recargar()
  }, [recargar])

  // Guardar lo pendiente al ocultarse la pestaña. Sin esto, escribir un campo y cerrar dentro de la
  // ventana de rebote pierde el cambio en silencio. Va con `keepalive` porque el navegador puede
  // descartar la página —y con ella una petición normal a medio camino— justo después de este
  // evento; `visibilitychange` a oculto es la última señal fiable que dan los navegadores móviles,
  // donde `beforeunload` no siempre llega.
  useEffect(() => {
    const alOcultar = () => {
      if (document.visibilityState === 'hidden' && pendiente.current) {
        void guardar({ alCerrar: true })
      }
    }
    document.addEventListener('visibilitychange', alOcultar)
    return () => document.removeEventListener('visibilitychange', alOcultar)
  }, [guardar])

  const valor = useMemo<EstadoPerfil>(
    () => ({ perfil, rup, existe, estado, error, guardadoEn, editar, guardarYa, ponerRup, marcar,
             marcarEn, registrarResultado,
             limpiar, recargar }),
    [perfil, rup, existe, estado, error, guardadoEn, editar, guardarYa, ponerRup, marcar, marcarEn,
     registrarResultado, limpiar, recargar],
  )

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export function usePerfil(): EstadoPerfil {
  const v = useContext(Contexto)
  if (!v) throw new Error('usePerfil fuera de <ProveedorPerfil>')
  return v
}

/** Cómo se llama la empresa cargada, para el masthead. Prioriza el RUP porque es el dato oficial. */
/**
 * Ajustes de perfiles guardados con nombres anteriores.
 *
 * Hoy solo uno: `avisos.umbral_encaje` pasó a `umbral_afinidad` cuando la cifra dejó de llamarse
 * «encaje». El perfil es un JSON opaco para el servidor —no hay migración de esquema que lo toque—,
 * así que la conversión ocurre aquí, al leerlo. Sin esto, quien tuviera el umbral en 85 lo vería en
 * 70 la próxima vez que abriera su perfil, sin ningún aviso y sin poder saber por qué.
 */
export function migrar(p: Perfil): Perfil {
  const viejo = p.avisos?.umbral_encaje
  if (viejo === undefined) return p
  const { umbral_encaje: _fuera, ...avisos } = p.avisos
  return { ...p, avisos: { ...avisos, umbral_afinidad: viejo } }
}

export function nombreEmpresa(perfil: Perfil, rup: RupDestilado | null): string | null {
  return rup?.empresa || perfil.razon_social || null
}
